import React, { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';

import type { ConfigContextType } from '../../../context/config.context';
import type { MarketContextType } from '../../../context/market.context';
import type { QueryStringContextType } from '../../../context/querystring.context';

import { Counter } from '../../Utility/Counter.ts';
import { ProductsUtility } from '../../Utility/ProductsUtility.ts';
import { SliceContainer } from '../SliceContainer/SliceContainer.tsx';
import { ConfigContext } from '../../../context/config.context.ts';
import { MarketContext } from '../../../context/market.context.ts';
import { QueryStringContext } from '../../../context/querystring.context.ts';
import { ExperimentContext } from '../../../context/experiment.context.ts';
import { ExperimentContextType } from '../../../context/experiment.context.ts';

import { getAlteredSlices } from '../../Utility/xsUtility/getAlteredSlices.ts';
import { useExposeCustomFilters } from '../../Utility/xsUtility/ExposeCustomFilter.tsx';

export interface SlicesContainerProps {
  slices: any;
  contentId?: string;
  category?: string;
  topLevelCategory?: string;
  region?: string;
  type: string;
}

export const SlicesContainer = ({
  slices: slicesInit,
  contentId = '',
  region,
  ...rest
}: SlicesContainerProps): JSX.Element | null => {
  const { translations } = useContext<ConfigContextType>(ConfigContext);
  const { pathname } = useLocation();
  const marketContext = useContext<MarketContextType>(MarketContext);
  const queryArgs = useContext<QueryStringContextType>(QueryStringContext);
  const { bucketedExperiments: experiments } =
    useContext<ExperimentContextType>(ExperimentContext);

  // Move all hooks to the top, before any early returns
  const market = `${marketContext.country}-${marketContext.language}`;

  // Collect all custom filters
  const customFilters = useMemo(() => {
    const filters: { experimentTextId: string; customFilter: any }[] = [];

    if (Object.keys(experiments).length) {
      for (const experiment in experiments) {
        const { attributes, experimentTextId } = experiments[experiment];

        if (attributes?.customFilter) {
          filters.push({
            experimentTextId,
            customFilter: attributes.customFilter,
          });
        }
      }
    }

    return filters;
  }, [experiments]);

  // Handle all custom filter exposures with a single hook
  useExposeCustomFilters(customFilters);

  // Now we can do early returns after all hooks
  if (!slicesInit || slicesInit.length <= 0) {
    return null;
  }

  let slices = _.cloneDeep(slicesInit);

  // Alter slices based on XS experiments
  if (Object.keys(experiments).length) {
    for (const experiment in experiments) {
      const { attributes, configuration, experimentTextId } =
        experiments[experiment];

      // skip experiment if it's not in the region
      if (
        region &&
        configuration?.regions &&
        !configuration.regions.includes(region as 'header' | 'footer' | 'body')
      ) {
        continue;
      }

      slices = getAlteredSlices(
        slices,
        attributes,
        configuration,
        experimentTextId,
        pathname,
        market,
        region,
        queryArgs,
      );
    }
  }

  // On Checkout Page, filter the slices
  if (
    ProductsUtility.isCheckoutPage(
      queryArgs,
      pathname,
      translations,
      marketContext.country,
      marketContext.language,
    )
  ) {
    switch (region) {
      // In header, do nothing
      case 'header':
        break;

      // In footer, remove all slices except FooterLegalSlice and FootnoteSlice
      case 'footer':
        slices = slices.filter(
          (slice: SlicesContainerProps['slices']) =>
            slice.__typename === 'FooterLegalSlice' ||
            slice.__typename === 'FootnoteSlice',
        );
        break;

      // In body, remove all slices except PlansDurationSlice
      case 'body':
        slices = slices.filter(
          (slice: SlicesContainerProps['slices']) =>
            slice.__typename === 'PlansDurationSlice' ||
            slice.__typename === 'PlansSlice',
        );
        break;

      default:
      // Do nothing;
    }
  }

  // Create a 1:1 relationship between PlansDurationSlice and PlansDisclaimerSlice
  // Collect disclaimer slices to add at the end
  const disclaimerSlices: SlicesContainerProps['slices'] = [];
  slices.forEach((slice: SlicesContainerProps['slices']) => {
    if (slice.__typename === 'PlansDurationSlice') {
      const disclaimerId = `plans-disclaimer-slice-${slice.id}`;
      disclaimerSlices.push({
        id: disclaimerId,
        __typename: 'PlansDisclaimerSlice',
        dataSourceSliceId: slice.id, // Add this to identify the source slice
        // Pass the slice data directly to avoid context dependency
        sourceSliceData: slice,
      });
    }
  });

  // Add all disclaimer slices at the end
  slices = [...slices, ...disclaimerSlices];

  // Applnanga-enabled StoryStream slice
  if (
    region === 'body' &&
    slices.length > 0 &&
    translations.STORYSTREAM_PATHS?.includes(pathname)
  ) {
    // Insert slice after slice with id located in list found in translations.STORYSTREAM_SLICE_IDS
    const index = slices.findIndex((slice: { id: string }) => {
      return translations.STORYSTREAM_SLICE_IDS?.includes(slice.id);
    });
    if (index >= 0) {
      const id = `storystream-slice-${Counter.getCounter('storystream-slice')}`;
      slices.splice(index + 1, 0, {
        id,
        __typename: 'StoryStreamSlice',
      });
    }
  }

  const sliceContainer = (slice: SlicesContainerProps['slices']) => {
    return (
      <SliceContainer
        key={slice.id}
        slice={slice}
        contentId={contentId}
        region={region}
        xsExperimentTextId={slice.xsExperimentTextId}
        {...rest}
      />
    );
  };

  const sliceContainerWithId = (slice: SlicesContainerProps['slices']) => {
    return slice.__typename === 'NavigationSlice' && slice.sticky ? (
      sliceContainer(slice)
    ) : (
      <div id={`slice-id--${slice.id}`}>{sliceContainer(slice)}</div>
    );
  };

  return (
    <>
      {slices.map((slice: SlicesContainerProps['slices']) => (
        <React.Fragment key={slice.id}>
          {sliceContainerWithId(slice)}
        </React.Fragment>
      ))}
    </>
  );
};
